import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useState,
} from 'react'

import {
  FilterStatus,
  PriceListParams,
  PriceListResponse,
} from '@/api/_schemas/management'
import { usePriceListInfinite } from '@/app/panel/management/price-list/_hooks/use-price-list'

type PriceListContextValues = {
  priceListInfinite: UseInfiniteQueryResult<InfiniteData<PriceListResponse>>
  priceList: PriceListResponse['data']
  hasPriceList: boolean
  filters: PriceListParams
  setFilters: React.Dispatch<React.SetStateAction<PriceListParams>>
  configureFilters: PriceListParams
  setConfigureFilters: React.Dispatch<React.SetStateAction<PriceListParams>>
  currentItem: string
  setCurrentItem: React.Dispatch<React.SetStateAction<string>>
  selectedValues: number[]
  toggleSelectedValue: (id: number) => void
  removeSelectedValue: (id: number) => void
  clearSelectedValues: () => void
}

type PriceListContextProviderProps = PropsWithChildren

export const PriceListContext = createContext<PriceListContextValues>(
  {} as PriceListContextValues,
)

type Action =
  | { type: 'ADD'; id: number }
  | { type: 'REMOVE'; id: number }
  | { type: 'CLEAR' }

function priceListReducer(state: number[], action: Action): number[] {
  switch (action.type) {
    case 'ADD':
      return [...state, action.id]
    case 'REMOVE':
      return state.filter((id) => id !== action.id)
    case 'CLEAR':
      return []
    default:
      return state
  }
}

const PriceListContextProvider = ({
  children,
}: PriceListContextProviderProps) => {
  const [selectedValues, dispatch] = useReducer(priceListReducer, [])

  const [filters, setFilters] = useState<PriceListParams>({})
  const [configureFilters, setConfigureFilters] = useState<PriceListParams>({
    status: FilterStatus.Ativo,
  })
  const [currentItem, setCurrentItem] = useState('')

  const priceListInfinite = usePriceListInfinite(filters)

  const priceList = useMemo(
    () =>
      priceListInfinite.data
        ? priceListInfinite.data.pages.flatMap((cur) => cur.data)
        : [],
    [priceListInfinite.data],
  )

  const hasPriceList = useMemo(
    () => !!priceListInfinite.data?.pages[0].meta.total,
    [priceListInfinite.data?.pages],
  )

  const toggleSelectedValue = useCallback(
    (itemId: number) => {
      const exists = selectedValues.some((id) => id === itemId)
      dispatch(
        exists ? { type: 'REMOVE', id: itemId } : { type: 'ADD', id: itemId },
      )
    },
    [selectedValues],
  )

  const removeSelectedValue = useCallback(
    (itemId: number) => {
      if (!selectedValues.some((id) => id === itemId)) return
      dispatch({ type: 'REMOVE', id: itemId })
    },
    [selectedValues],
  )

  const clearSelectedValues = useCallback(() => {
    dispatch({ type: 'CLEAR' })
  }, [])

  return (
    <PriceListContext.Provider
      value={{
        priceListInfinite,
        priceList,
        hasPriceList,
        filters,
        setFilters,
        configureFilters,
        setConfigureFilters,
        currentItem,
        setCurrentItem,
        selectedValues,
        toggleSelectedValue,
        removeSelectedValue,
        clearSelectedValues,
      }}
    >
      {children}
    </PriceListContext.Provider>
  )
}

function usePriceListContext() {
  return useContext(PriceListContext)
}

export { PriceListContextProvider, usePriceListContext }
